<template>
  <div v-if="items.length > 0 && !hidden">
    <!-- DESKTOP: bottom-right panel -->
    <section v-if="!isPhone" class="rx-up" role="region" aria-label="Uploads">
      <header class="rx-up__head">
        <span class="rx-up__title" aria-live="polite">{{ headline }}</span>
        <button
          type="button"
          class="rx-up__icon-btn"
          :aria-label="collapsed ? 'Expand uploads' : 'Collapse uploads'"
          :aria-expanded="String(!collapsed)"
          @click="collapsed = !collapsed"><i :class="['fas', collapsed ? 'fa-chevron-up' : 'fa-chevron-down']"></i></button>
        <button
          type="button"
          class="rx-up__icon-btn"
          aria-label="Close and clear finished uploads"
          :disabled="active > 0"
          :title="active > 0 ? 'Uploads are still in progress' : 'Close'"
          @click="close"><i class="fas fa-times"></i></button>
      </header>
      <ul v-if="!collapsed" class="rx-up__list">
        <li v-for="it in items" :key="it.id" class="rx-up__row">
          <span class="rx-up__thumb">
            <img v-if="it.thumbUrl && !broken[it.id]" :src="it.thumbUrl" alt="" @error="markBroken(it)" />
            <i v-else :class="iconOf(it).cls" :style="{ color: iconOf(it).color }"></i>
          </span>
          <span class="rx-up__body">
            <span class="rx-up__name" :title="it.relativePath">{{ it.fileName }}<span v-if="modeLabel(it)" class="rx-up__badge">{{ modeLabel(it) }}</span></span>
            <span class="rx-up__dest" :title="it.errorDetail || (it.error ? it.error : it.destLabel)">{{ it.error ? it.error : it.destLabel }}</span>
            <details v-if="it.errorDetail" class="rx-up__detail"><summary>Why?</summary>{{ it.errorDetail }}</details>
            <span v-if="it.status === 'uploading' || it.status === 'queued'" class="rx-up__bar"><span class="rx-up__fill" :style="{ width: it.progress + '%' }"></span></span>
          </span>
          <i :class="['rx-up__status', statusIcon(it)]" :title="statusText(it)" :aria-label="statusText(it)"></i>
          <button v-if="it.status === 'failed' && !it.noRetry" type="button" class="rx-up__act" :aria-label="'Retry ' + it.fileName" @click="retry(it)">Retry</button>
          <button v-else-if="isActive(it)" type="button" class="rx-up__act" :aria-label="'Cancel ' + it.fileName" @click="cancel(it)">Cancel</button>
        </li>
      </ul>
    </section>

    <!-- PHONE: full-width bar; tap opens a bottom sheet with the list -->
    <template v-else>
      <button type="button" class="rx-upbar" aria-haspopup="dialog" @click="sheetOpen = true">
        <i :class="['rx-upbar__icon', active > 0 ? 'fas fa-cloud-upload-alt' : (failed > 0 ? 'fas fa-exclamation-circle' : 'fas fa-check-circle')]"></i>
        <span class="rx-upbar__text" aria-live="polite">{{ headline }}</span>
        <i class="fas fa-chevron-up rx-upbar__chev"></i>
        <span class="rx-upbar__progress" :style="{ width: overallProgress + '%' }"></span>
      </button>
      <BottomSheet v-if="sheetOpen" :title="headline" @close="sheetOpen = false">
        <ul class="rx-up__list rx-up__list--phone">
          <li v-for="it in items" :key="it.id" class="rx-up__row rx-up__row--phone">
            <span class="rx-up__thumb">
              <img v-if="it.thumbUrl && !broken[it.id]" :src="it.thumbUrl" alt="" @error="markBroken(it)" />
              <i v-else :class="iconOf(it).cls" :style="{ color: iconOf(it).color }"></i>
            </span>
            <span class="rx-up__body">
              <span class="rx-up__name">{{ it.fileName }}<span v-if="modeLabel(it)" class="rx-up__badge">{{ modeLabel(it) }}</span></span>
              <span class="rx-up__dest">{{ it.error ? it.error : it.destLabel }}</span>
              <details v-if="it.errorDetail" class="rx-up__detail"><summary>Why?</summary>{{ it.errorDetail }}</details>
              <span v-if="it.status === 'uploading' || it.status === 'queued'" class="rx-up__bar"><span class="rx-up__fill" :style="{ width: it.progress + '%' }"></span></span>
            </span>
            <i :class="['rx-up__status', statusIcon(it)]" :aria-label="statusText(it)"></i>
            <button v-if="it.status === 'failed' && !it.noRetry" type="button" class="rx-up__act rx-up__act--phone" @click="retry(it)">Retry</button>
            <button v-else-if="isActive(it)" type="button" class="rx-up__act rx-up__act--phone" @click="cancel(it)">Cancel</button>
          </li>
        </ul>
        <div class="rx-up__sheet-foot">
          <button type="button" class="rx-up__sheet-btn" :disabled="active > 0" @click="close">{{ active > 0 ? 'Uploading…' : 'Clear' }}</button>
          <button type="button" class="rx-up__sheet-btn rx-up__sheet-btn--primary" @click="sheetOpen = false">Done</button>
        </div>
      </BottomSheet>
    </template>
  </div>
</template>

<script>
import BottomSheet from './BottomSheet.vue'
import { uploadQueueState, isActive, cancelItem, retryItem, clearFinished, fileIconFor, MODE_LABELS } from './uploadQueue'

var STATUS = {
  queued: { icon: 'far fa-clock', text: 'Waiting' },
  uploading: { icon: 'fas fa-spinner fa-spin', text: 'Uploading' },
  done: { icon: 'fas fa-check-circle', text: 'Uploaded' },
  failed: { icon: 'fas fa-exclamation-circle', text: 'Failed' },
  canceled: { icon: 'fas fa-ban', text: 'Canceled' }
}

export default {
  name: 'UploadTray',
  components: { BottomSheet },
  props: {
    isPhone: { type: Boolean, default: false },
    // Phone: hidden while another bottom bar (select mode) owns the bottom edge
    hidden: { type: Boolean, default: false }
  },
  data () {
    return {
      queue: uploadQueueState,
      collapsed: false,
      sheetOpen: false,
      broken: {}
    }
  },
  computed: {
    items () {
      return this.queue.items
    },
    active () {
      return this.items.filter(isActive).length
    },
    done () {
      return this.items.filter(function (it) { return it.status === 'done' }).length
    },
    failed () {
      return this.items.filter(function (it) { return it.status === 'failed' }).length
    },
    headline () {
      var total = this.items.length
      if (this.active > 0) {
        var finished = total - this.active
        return 'Uploading ' + Math.min(finished + 1, total) + ' of ' + total
      }
      if (this.done === 0 && this.failed === 0) return total === 1 ? 'Upload canceled' : 'Uploads canceled'
      var text = this.done + (this.done === 1 ? ' upload complete' : ' uploads complete')
      if (this.failed > 0) text += ' · ' + this.failed + ' failed'
      return text
    },
    overallProgress () {
      if (this.items.length === 0) return 0
      var sum = 0
      this.items.forEach(function (it) {
        sum += isActive(it) ? it.progress : 100
      })
      return Math.round(sum / this.items.length)
    }
  },
  watch: {
    'queue.items': function (list) {
      if (list.length === 0) this.sheetOpen = false
    }
  },
  methods: {
    isActive (it) {
      return isActive(it)
    },
    modeLabel (it) {
      return MODE_LABELS[it.mode] || ''
    },
    iconOf (it) {
      return fileIconFor(it.fileName)
    },
    statusIcon (it) {
      return (STATUS[it.status] || STATUS.queued).icon + ' is-' + it.status
    },
    statusText (it) {
      return (STATUS[it.status] || STATUS.queued).text
    },
    markBroken (it) {
      this.$set(this.broken, it.id, true)
    },
    retry (it) {
      retryItem(it)
    },
    cancel (it) {
      cancelItem(it)
    },
    close () {
      if (this.active > 0) return
      clearFinished()
      this.broken = {}
      this.collapsed = false
      this.sheetOpen = false
    }
  }
}
</script>

<style scoped>
.rx-up__badge {
  display: inline-block;
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 999px;
  background: #e3f1ef;
  color: #1a7f86;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  vertical-align: middle;
}
.rx-up__detail {
  font-size: 12px;
  color: #4b5563;
  white-space: normal;
}
.rx-up__detail summary {
  cursor: pointer;
  color: #1f2937;
}
.rx-up {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1040;
  width: 360px;
  max-width: calc(100vw - 32px);
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  color: #1f2937;
  overflow: hidden;
}
.rx-up__head {
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 0 6px 0 16px;
  background: #fff;
  color: #1f2937;
  border-bottom: 1px solid #e2e5ea;
}
.rx-up__title {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.rx-up__icon-btn {
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #4b5563;
}
.rx-up__icon-btn:hover {
  background: #f3f4f6;
}
.rx-up__icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.rx-up__list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 320px;
  overflow-y: auto;
}
.rx-up__list--phone {
  max-height: none;
}
.rx-up__row {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #eef0f2;
}
.rx-up__row--phone {
  min-height: 64px;
  padding: 8px 16px;
}
.rx-up__thumb {
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  margin-right: 10px;
  border-radius: 4px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-size: 18px;
}
.rx-up__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.rx-up__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.rx-up__name {
  font-size: 14px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.rx-up__dest {
  font-size: 12px;
  color: #6b7280;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.rx-up__bar {
  display: block;
  height: 4px;
  margin-top: 4px;
  border-radius: 2px;
  background: #e5e7eb;
  overflow: hidden;
}
.rx-up__fill {
  display: block;
  height: 100%;
  background: #1a7f86;
  transition: width 0.2s;
}
.rx-up__status {
  flex: 0 0 auto;
  margin-left: 8px;
  color: #6b7280;
}
.rx-up__status.is-done {
  color: #1e7e34;
}
.rx-up__status.is-failed {
  color: #b91c1c;
}
.rx-up__status.is-uploading {
  color: #1a7f86;
}
.rx-up__act {
  flex: 0 0 auto;
  margin-left: 8px;
  height: 30px;
  padding: 0 10px;
  border: 1px solid #e2e5ea;
  border-radius: 6px;
  background: #fff;
  color: #1a7f86;
  font-size: 13px;
  font-weight: 600;
}
.rx-up__act--phone {
  min-width: 64px;
  height: 44px;
  font-size: 15px;
}
.rx-upbar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1030;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: calc(56px + env(safe-area-inset-bottom));
  padding: 0 16px env(safe-area-inset-bottom);
  border: 0;
  border-top: 1px solid #e2e5ea;
  background: #fff;
  color: #1f2937;
  font-size: 16px;
  text-align: left;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}
.rx-upbar__icon {
  margin-right: 12px;
  color: #1a7f86;
  font-size: 18px;
}
.rx-upbar__text {
  flex: 1 1 auto;
  min-width: 0;
  font-weight: 600;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.rx-upbar__chev {
  color: #6b7280;
}
.rx-upbar__progress {
  position: absolute;
  left: 0;
  top: 0;
  height: 3px;
  background: #1a7f86;
  transition: width 0.2s;
}
.rx-up__sheet-foot {
  display: flex;
  padding: 12px 16px 16px;
}
.rx-up__sheet-btn {
  flex: 1 1 0;
  min-height: 48px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #fff;
  color: #1f2937;
  font-size: 16px;
  font-weight: 600;
}
.rx-up__sheet-btn + .rx-up__sheet-btn {
  margin-left: 12px;
}
.rx-up__sheet-btn--primary {
  background: #1e7e34;
  border-color: #1e7e34;
  color: #fff;
}
.rx-up__sheet-btn:disabled {
  opacity: 0.5;
}
</style>
